import { DateTime } from "luxon";

import { DashboardTargetEntryDTO } from "../../../DTOs/Dashboards/DashboardTargetsCardDataDTO";
import {
  MaterialMovementCardDataDTO,
  MaterialMovementCardSectionDTO,
} from "../../../DTOs/Dashboards/MaterialMovementCardDataDTO";

export type TargetMetricKind = "additive" | "rate";

export type TargetGaugeStatus = "no-target" | "on-track" | "behind";

export interface TargetGaugeInput {
  key: string;
  label: string;
  unit: string;
  kind: TargetMetricKind;
  target: number | null;
  actual: number;
  elapsed: number;
}

export interface TargetGauge {
  key: string;
  label: string;
  unit: string;
  kind: TargetMetricKind;
  target: number | null;
  actual: number;
  attainment: number | null;
  expected: number | null;
  fill: number;
  status: TargetGaugeStatus;
}

const TOLERANCE = 0.01; // Note: a shortfall this small is rounding in the aggregates rather than a mine falling behind.

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export const elapsedFraction = (start: DateTime, end: DateTime, now: DateTime): number => {
  const span = end.toMillis() - start.toMillis();
  if (span <= 0) return 0;

  return clamp((now.toMillis() - start.toMillis()) / span, 0, 1);
};

export const buildTargetGauge = ({
  key,
  label,
  unit,
  kind,
  target,
  actual,
  elapsed,
}: TargetGaugeInput): TargetGauge => {
  const base = { key, label, unit, kind, target, actual };

  if (target === null || target <= 0)
    return { ...base, attainment: null, expected: null, fill: 0, status: "no-target" };

  const attainment = actual / target;
  const expected = kind === "additive" ? clamp(elapsed, 0, 1) : null;
  const threshold = expected ?? 1;

  return {
    ...base,
    attainment,
    expected,
    fill: clamp(attainment, 0, 1),
    status: attainment >= threshold - TOLERANCE ? "on-track" : "behind",
  };
};

export interface TargetActualSources {
  materialMovement: MaterialMovementCardDataDTO | null;
}

const sectionRow = (section: MaterialMovementCardSectionDTO | undefined, label: string) =>
  section?.rows?.find(row => row.label === label)?.both ?? null;

const sectionTotal = (section: MaterialMovementCardSectionDTO | undefined) =>
  section ? section.rows.reduce((total, row) => total + (row.both ?? 0), 0) : null;

const ACTUAL_SOURCES: Record<string, (sources: TargetActualSources) => number | null> = {
  "ore-tonnes": ({ materialMovement }) => sectionRow(materialMovement?.production, "Ore"),
  "waste-tonnes": ({ materialMovement }) => sectionRow(materialMovement?.production, "Waste"),
  "rehandle-tonnes": ({ materialMovement }) => sectionTotal(materialMovement?.rehandle),
  "crushing-tonnes": ({ materialMovement }) => sectionTotal(materialMovement?.crushing),
};

export const buildTargetGauges = (
  targets: DashboardTargetEntryDTO[],
  sources: TargetActualSources,
  elapsed: number
): TargetGauge[] =>
  targets.flatMap(entry => {
    const actual = ACTUAL_SOURCES[entry.key]?.(sources) ?? null;
    if (actual === null) return [];

    return buildTargetGauge({
      key: entry.key,
      label: entry.label,
      unit: entry.unit,
      kind: entry.isRate ? "rate" : "additive",
      target: entry.target,
      actual,
      elapsed,
    });
  });

// Note: null means every metric is shown, so the stored selection does not go stale when the set of
// metrics changes. Turning the last one back on returns to null rather than a list that happens to
// hold them all.
export const toggleGaugeKey = (
  shownKeys: string[] | null,
  allKeys: string[],
  key: string
): string[] | null => {
  const shown = shownKeys ?? allKeys;
  const next = shown.includes(key)
    ? shown.filter(shownKey => shownKey !== key)
    : allKeys.filter(allKey => shown.includes(allKey) || allKey === key);

  return next.length === allKeys.length ? null : next;
};
